import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE_URL, formatPrice } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { fetchProduct, fetchCreatorsForProduct } from "@/lib/queries";
import { fetchRelatedProducts } from "@/lib/recommendations.functions";
import { ProductCard } from "@/components/product-card";
import { ArrowUpRight, Heart, Plus, ShieldCheck, Share2 } from "lucide-react";
import { resolveImage } from "@/lib/image-map";
import { RouteError, RouteNotFound } from "@/components/route-fallbacks";
import { ProductPageSkeleton } from "@/components/skeleton-card";
import { ImageZoom } from "@/components/image-zoom";
import { LiveCursors } from "@/components/live-cursors";
import { shareOrCopy } from "@/lib/share";
import { useAuth } from "@/hooks/use-auth";
import { useRoles } from "@/hooks/use-roles";
import { fetchCreatorForUser } from "@/lib/queries";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { submitProductReport } from "@/lib/affiliate.functions";


export const Route = createFileRoute("/product/$id")({
  loader: ({ params }) => fetchProduct(params.id),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  head: ({ params, loaderData }) => {
    const p = loaderData;
    const url = `${SITE_URL}/product/${params.id}`;
    if (!p) {
      return {
        meta: [
          { title: "Product not found — Pluggz" },
          { name: "description", content: "This product is no longer in the Pluggz catalog. Discover similar editor-picked pieces from vetted creators." },
          { property: "og:url", content: url },
          { property: "og:type", content: "website" },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const title = `${p.title} by ${p.brand} — Pluggz`;
    const desc = (p.description?.trim() ||
      `${p.title} from ${p.brand} — ${p.category.toLowerCase()} pick at ${formatPrice(p.price_cents, p.currency) || `£${p.price}`}, curated by creators on Pluggz.`
    ).slice(0, 160);
    const image = resolveImage(p.image_key);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: `${p.title} — ${p.brand}` },
        { property: "og:description", content: desc },
        { property: "og:type", content: "product" },
        { property: "og:url", content: url },
        { property: "og:image", content: image },
        { property: "product:price:amount", content: String((p.price_cents ?? 0) / 100 || p.price) },
        { property: "product:price:currency", content: p.currency || "GBP" },
        { property: "product:brand", content: p.brand },
        { property: "product:availability", content: p.in_stock ? "in stock" : "out of stock" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${p.title} — ${p.brand}` },
        { name: "twitter:description", content: desc },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: p.title,
            image: [image],
            description: desc,
            brand: { "@type": "Brand", name: p.brand },
            category: p.category,
            offers: {
              "@type": "Offer",
              url,
              priceCurrency: p.currency || "GBP",
              price: (p.price_cents ?? 0) / 100 || p.price,
              availability: p.in_stock
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
            },
            ...(p.rating
              ? {
                  aggregateRating: {
                    "@type": "AggregateRating",
                    ratingValue: p.rating,
                    reviewCount: p.ratings_total ?? 1,
                  },
                }
              : {}),
          }),
        },
      ],
    };
  },
  component: ProductPage,
});

function ProductPage() {
  const { id } = Route.useParams();
  const initial = Route.useLoaderData();
  const { data: product = initial, isLoading } = useQuery({
    queryKey: ["product", id],
    queryFn: () => fetchProduct(id),
    initialData: initial ?? undefined,
  });
  const { data: related } = useQuery({
    queryKey: ["related-products", id],
    queryFn: () => fetchRelatedProducts({ data: { productId: id, limit: 8 } }),
    staleTime: 10 * 60_000,
  });
  const { data: creators = [], isLoading: creatorsLoading } = useQuery({
    queryKey: ["product-creators", id],
    queryFn: () => fetchCreatorsForProduct(id),
    enabled: !!product,
  });

  const { user } = useAuth();
  const { isCreator } = useRoles();
  const { data: myCreator } = useQuery({
    queryKey: ["my-creator", user?.id],
    queryFn: () => fetchCreatorForUser(user!.id),
    enabled: !!user && isCreator,
  });


  if (isLoading) return <ProductPageSkeleton />;
  if (!product) return (
    <div className="mx-auto max-w-xl px-6 py-32 text-center">
      <h1 className="font-serif text-4xl">Not in the catalog</h1>
      <Link to="/discover" className="mt-6 inline-block text-sm text-accent">Back to discover →</Link>
    </div>
  );

  const similar = (related?.products ?? []).slice(0, 4);

  return (
    <div>
      <section className="relative grid grid-cols-1 md:grid-cols-2">
        <LiveCursors roomId={`product:${product.id}`} />
        <div className="group relative aspect-[4/5] overflow-hidden bg-muted md:aspect-auto">
          <ImageZoom src={resolveImage(product.image_key)} alt={product.title} className="ken-burns h-full w-full object-cover" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-background/20 via-transparent to-transparent" />
        </div>
        <div className="flex flex-col justify-center px-6 py-12 md:px-16">
          <p className="editorial-eyebrow">{product.brand}</p>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl">{product.title}</h1>
          {product.description && (
            <p className="text-muted-foreground leading-relaxed mt-3">{product.description}</p>
          )}
          {product.rating != null && (
            <div className="mt-2 flex items-center gap-1.5">
              <span className="text-sm">
                {"★".repeat(Math.round(product.rating))}{"☆".repeat(5 - Math.round(product.rating))}
              </span>
              <span className="text-xs text-muted-foreground">({product.ratings_total ?? 0} reviews)</span>
            </div>
          )}
          <p className="mt-4 font-serif text-2xl shimmer inline-block">{formatPrice(product.price_cents, product.currency) || `£${product.price}`}</p>

          <div className="mt-10 flex gap-3">
            {product.in_stock ? (
              <a href={product.url} target="_blank" rel="noopener noreferrer sponsored" className="group inline-flex flex-1 items-center justify-center gap-2 bg-foreground px-6 py-4 text-sm uppercase tracking-widest text-background hover:bg-accent transition">
                Buy on {product.brand}
                <ArrowUpRight className="h-4 w-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            ) : (
              <button disabled className="flex-1 bg-muted px-6 py-4 text-sm uppercase tracking-widest text-muted-foreground cursor-not-allowed">
                Sold out — see similar
              </button>
            )}
            {user && isCreator && myCreator && (
              <button
                type="button"
                onClick={async () => {
                  const { data: existing } = await supabase
                    .from("creator_products")
                    .select("id")
                    .eq("creator_id", myCreator.id)
                    .eq("product_id", product.id)
                    .maybeSingle();
                  if (existing) {
                    toast("Already on your storefront");
                    return;
                  }
                  const { error } = await supabase
                    .from("creator_products")
                    .upsert(
                      { creator_id: myCreator.id, product_id: product.id },
                      { onConflict: "creator_id,product_id" }
                    );
                  if (error) {
                    toast.error("Could not add to storefront");
                  } else {
                    toast.success("Added to your storefront");
                  }
                }}
                className="inline-flex items-center justify-center gap-1 border border-border px-4 text-sm uppercase tracking-widest hover:bg-card transition"
              >
                <Plus className="h-4 w-4" /> Add to storefront
              </button>
            )}
            <button
              type="button"
              aria-label="Share this product"
              onClick={() =>
                shareOrCopy({
                  title: product.title,
                  text: `${product.title} by ${product.brand} — curated on Pluggz`,
                  url: `${SITE_URL}/product/${id}`,
                })
              }
              className="inline-flex items-center gap-2 border border-border px-4 py-2 text-xs uppercase tracking-widest hover:bg-card transition"
            >
              <Share2 className="h-4 w-4" /> Share
            </button>
            <button aria-label="Save to wishlist" className="border border-border px-4 hover:bg-card transition">
              <Heart className="h-5 w-5" />
            </button>
          </div>

          <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" /> Secure checkout on the brand's own site · Two-tap rule
          </p>

          {product.commission && product.commission > 0 && (
            <div className="mt-4 text-xs uppercase tracking-widest text-muted-foreground">
              Est. commission rate <span className="font-medium">{product.commission}%</span>
            </div>
          )}
          <div className="mt-12 hairline pt-6 text-xs text-muted-foreground">
            Affiliated via {product.network} · Pluggz earns a small commission on this sale.
          </div>
        </div>
      </section>

      {(creatorsLoading || creators.length > 0) && (
        <section className="mx-auto max-w-7xl px-6 pt-16">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Curated by</p>
          <h2 className="mt-2 font-serif text-3xl">Creators who love this</h2>
          <div className="mt-6 flex gap-4 overflow-x-auto snap-x snap-mandatory md:flex-wrap md:overflow-visible">
            {creatorsLoading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex-shrink-0 w-20 text-center">
                    <div className="h-12 w-12 rounded-full bg-muted animate-pulse mx-auto" />
                  </div>
                ))
              : creators.map((creator) => (
                  <Link
                    key={creator.id}
                    to="/creator/$handle"
                    params={{ handle: creator.handle }}
                    className="flex-shrink-0 w-20 snap-start text-center"
                  >
                    <img
                      src={creator.avatar_url || ""}
                      alt={creator.name}
                      className="h-12 w-12 rounded-full object-cover border border-border mx-auto"
                    />
                    <p className="mt-2 text-sm font-medium truncate">{creator.name}</p>
                    <p className="text-xs text-muted-foreground truncate">@{creator.handle}</p>
                  </Link>
                ))}
          </div>
        </section>
      )}

      {similar.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 pt-24 pb-24">
          <p className="editorial-eyebrow">You might also like</p>
          <h2 className="mt-2 font-serif text-3xl">More like this</h2>
          <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-14 md:grid-cols-4">
            {similar.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {user && <ReportProductLink productId={product.id} />}
    </div>
  );
}

const REPORT_REASONS = [
  { value: "out_of_stock", label: "Out of stock" },
  { value: "wrong_price", label: "Wrong price shown" },
  { value: "broken_link", label: "Broken or incorrect link" },
  { value: "inappropriate", label: "Inappropriate content" },
  { value: "other", label: "Other" },
] as const;

function ReportProductLink({ productId }: { productId: string }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<(typeof REPORT_REASONS)[number]["value"]>("out_of_stock");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = useServerFn(submitProductReport);

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      await submit({ data: { productId, reason, notes: notes.trim() || undefined } });
      toast.success("Thanks for letting us know. We'll review this shortly.");
      setOpen(false);
      setNotes("");
      setReason("out_of_stock");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not submit report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="mx-auto max-w-7xl px-6 pb-12 text-center">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
        >
          Report this product
        </button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Report a problem</DialogTitle>
          </DialogHeader>
          <RadioGroup value={reason} onValueChange={(v) => setReason(v as typeof reason)} className="space-y-2">
            {REPORT_REASONS.map((r) => (
              <div key={r.value} className="flex items-center gap-3">
                <RadioGroupItem value={r.value} id={`reason-${r.value}`} />
                <Label htmlFor={`reason-${r.value}`} className="text-sm font-normal cursor-pointer">{r.label}</Label>
              </div>
            ))}
          </RadioGroup>
          <div>
            <Label htmlFor="report-notes" className="text-xs uppercase tracking-widest text-muted-foreground">
              Additional notes (optional)
            </Label>
            <textarea
              id="report-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={1000}
              rows={3}
              className="mt-2 w-full border border-border bg-card px-3 py-2 text-sm outline-none focus:border-foreground"
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="border border-border px-4 py-2 text-xs uppercase tracking-widest hover:bg-card"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={submitting}
              className="bg-foreground px-4 py-2 text-xs uppercase tracking-widest text-background hover:bg-accent disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Submit report"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
